/**
 * Display metadata for permission modules.
 * The backend stores permissions as `module.resource.action`. This maps the raw
 * module key to a friendly label, icon and accent so the Users & Permissions
 * screens can render a consistent, readable matrix.
 */
export const MODULE_META = {
  rbac: { label: "Access Control (RBAC)", icon: "🛡️", accent: "#f5a51e" },
  user: { label: "User Management", icon: "👥", accent: "#2563eb" },
  system: { label: "System Settings", icon: "⚙️", accent: "#6b7280" },
  booking: { label: "Bookings", icon: "📅", accent: "#0ea5e9" },
  people: { label: "People", icon: "🧑", accent: "#8b5cf6" },
  supplier: { label: "Suppliers", icon: "🚚", accent: "#0d9488" },
  finance: { label: "Finance", icon: "💲", accent: "#16a34a" },
  document: { label: "Documents", icon: "📄", accent: "#ea580c" },
  pricing: { label: "Pricing & Rules", icon: "🏷️", accent: "#db2777" },
  markup: { label: "Markups", icon: "📈", accent: "#d946ef" },
  flight: { label: "Products · Flights", icon: "✈️", accent: "#2563eb" },
  hotel: { label: "Products · Hotels", icon: "🏨", accent: "#7c3aed" },
  holiday: { label: "Holidays", icon: "🏖️", accent: "#f59e0b" },
  geo: { label: "Geo Data", icon: "🌍", accent: "#0891b2" },
  terminal: { label: "Airports / Terminals", icon: "🛫", accent: "#0284c7" },
  layout: { label: "CMS · Layout", icon: "🧩", accent: "#9333ea" },
  cms: { label: "CMS · Content", icon: "📝", accent: "#c026d3" },
};

/** Preferred module display order in the matrix / catalog. */
export const MODULE_ORDER = [
  "rbac",
  "user",
  "system",
  "booking",
  "people",
  "supplier",
  "finance",
  "document",
  "pricing",
  "markup",
  "flight",
  "hotel",
  "holiday",
  "geo",
  "terminal",
  "layout",
  "cms",
];

/** Color chips per action verb. */
export const ACTION_META = {
  view: { label: "View", color: "#2563eb", bg: "#dbeafe" },
  create: { label: "Create", color: "#16a34a", bg: "#dcfce7" },
  update: { label: "Update", color: "#d97706", bg: "#fef3c7" },
  delete: { label: "Delete", color: "#dc2626", bg: "#fee2e2" },
  assign: { label: "Assign", color: "#7c3aed", bg: "#ede9fe" },
};

export const getModuleMeta = (key) =>
  MODULE_META[key] || { label: humanize(key), icon: "🔑", accent: "#6b7280" };

export const getActionMeta = (action) =>
  ACTION_META[action] || { label: humanize(action), color: "#6b7280", bg: "#f3f4f6" };

/** Turn `flight_status` / `create` into `Flight Status` / `Create`. */
export function humanize(str = "") {
  return String(str)
    .replace(/[_\-.]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Group a flat permission list into ordered modules:
 * [{ key, label, icon, accent, permissions: [...] }]
 */
export function groupPermissionsByModule(permissions = []) {
  const groups = {};
  for (const p of permissions) {
    if (!groups[p.module]) groups[p.module] = [];
    groups[p.module].push(p);
  }
  const orderedKeys = [
    ...MODULE_ORDER.filter((k) => groups[k]),
    ...Object.keys(groups).filter((k) => !MODULE_ORDER.includes(k)).sort(),
  ];
  return orderedKeys.map((key) => {
    const meta = getModuleMeta(key);
    return {
      key,
      label: meta.label,
      icon: meta.icon,
      accent: meta.accent,
      permissions: groups[key].slice().sort((a, b) => a.key.localeCompare(b.key)),
    };
  });
}
